// ============================================
// src/components/meetings/ActionItemsManagement.js
// Action Items Management for Admin - Post Meeting Updates
// ============================================

// src/components/meetings/ActionItemsManagement.js
// Action Items Management for Admin - Post Meeting Updates
// Updated with sidebar navigation and consistent styling

import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase.config';
import '../../pages/UserDashboard.css';
import './ActionItemsManagement.css';

function ActionItemsManagement({ userData, onBack }) {
  const [actionItems, setActionItems] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [selectedMeeting, setSelectedMeeting] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [updateData, setUpdateData] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');

  const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';

  useEffect(() => {
    fetchMeetings();
    fetchActionItems();
    if (darkMode) {
      document.body.classList.add('dark-mode');
    }
  }, [darkMode]);

  useEffect(() => {
    fetchActionItems();
  }, [selectedMeeting, selectedStatus]);

  const fetchMeetings = async () => {
    try {
      const { data, error } = await supabase
        .from('meetings')
        .select('id, title, meeting_date')
        .order('meeting_date', { ascending: false });

      if (error) throw error;
      setMeetings(data || []);
    } catch (error) {
      console.error('Error fetching meetings:', error);
    }
  };

  const fetchActionItems = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('action_items')
        .select(`
          *,
          meetings (
            title,
            meeting_date
          )
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (selectedMeeting !== 'all') {
        query = query.eq('meeting_id', selectedMeeting);
      }
      if (selectedStatus !== 'all') {
        query = query.eq('status', selectedStatus);
      }

      const { data, error } = await query;

      if (error) throw error;
      setActionItems(data || []);
    } catch (error) {
      console.error('Error fetching action items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item.id);
    setUpdateData({
      description: item.description,
      assigned_to: item.assigned_to,
      status: item.status,
      priority: item.priority,
      due_date: item.due_date || '',
      completion_notes: item.completion_notes || ''
    });
  };

  const handleUpdate = async (itemId) => {
    try {
      const { error } = await supabase
        .from('action_items')
        .update({
          ...updateData,
          updated_at: new Date().toISOString()
        })
        .eq('id', itemId);

      if (error) throw error;

      alert('Action item updated successfully!');
      setEditingItem(null);
      setUpdateData({});
      fetchActionItems();
    } catch (error) {
      console.error('Error updating action item:', error);
      alert('Failed to update action item');
    }
  };

  const handleQuickStatusUpdate = async (itemId, newStatus) => {
    try {
      const { data: existingItem, error: fetchError } = await supabase
        .from('action_items')
        .select('*')
        .eq('id', itemId)
        .single();
      
      if (fetchError) {
        console.error('Error fetching action item:', fetchError);
        alert('Failed to fetch action item: ' + fetchError.message);
        return;
      }

      if (!existingItem) {
        alert('Action item not found');
        return;
      }

      const updatePayload = {
        status: newStatus,
        updated_at: new Date().toISOString()
      };

      if (newStatus === 'completed') {
        updatePayload.completed_at = new Date().toISOString();
        updatePayload.completed_by = userData?.id || null;
      }

      const { data, error } = await supabase
        .from('action_items')
        .update(updatePayload)
        .eq('id', itemId)
        .select();

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        throw new Error('Update successful but no data returned');
      }

      fetchActionItems();
      alert(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this action item?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('action_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      alert('Action item deleted successfully!');
      fetchActionItems();
    } catch (error) {
      console.error('Error deleting action item:', error);
      alert('Failed to delete action item');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#ffc107';
      case 'in_progress': return '#17a2b8';
      case 'completed': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return '#dc3545';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    document.body.classList.toggle('dark-mode');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  if (!isAdmin) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-content" style={{ textAlign: 'center', padding: '50px' }}>
          <h3>Access Denied</h3>
          <p>Only administrators can manage action items.</p>
          <button onClick={onBack} className="btn-secondary" style={{ marginTop: '20px' }}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard-container action-items-container ${darkMode ? 'dark-mode' : ''}`}>
      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>

      {/* Sidebar Navigation */}
      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar">
              {userData?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <h3>{userData?.name}</h3>
              <p>Flat {userData?.flat_number}</p>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-menu">
          <button
            className="menu-item"
            onClick={onBack}
          >
            <span className="menu-icon">🏠</span>
            <span className="menu-label">Back to Meetings</span>
          </button>
          
          <div className="sidebar-divider"></div>
          
          <div className="filter-section">
            <label className="filter-label">Filter by Meeting</label>
            <select
              value={selectedMeeting}
              onChange={(e) => setSelectedMeeting(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Meetings</option>
              {meetings.map(meeting => (
                <option key={meeting.id} value={meeting.id}>
                  {meeting.title} ({new Date(meeting.meeting_date).toLocaleDateString()})
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-section">
            <label className="filter-label">Filter by Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </nav>
        
        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleDarkMode}>
            <span className="menu-icon">{darkMode ? '☀️' : '🌙'}</span>
            <span className="menu-label">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="dashboard-main">
        {/* Mobile Header */}
        <header className="dashboard-header enhanced">
          <div className="header-left">
            <button 
              className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <h2 className="page-title-mobile">📋 Action Items</h2>
          </div>
        </header>

        <div className="dashboard-content">
          {/* Desktop Header */}
          <div className="action-items-header">
            <h2 className="page-title">📋 Action Items Management</h2>
          </div>

          {/* Stats Cards */}
          <div className="dashboard-grid stats-grid">
            <div className="dashboard-card stat-card gradient-purple">
              <div className="stat-content">
                <h3>{actionItems.length}</h3>
                <small>Total Items</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-yellow">
              <div className="stat-content">
                <h3>{actionItems.filter(a => a.status === 'pending').length}</h3>
                <small>Pending</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-cyan">
              <div className="stat-content">
                <h3>{actionItems.filter(a => a.status === 'in_progress').length}</h3>
                <small>In Progress</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-green">
              <div className="stat-content">
                <h3>{actionItems.filter(a => a.status === 'completed').length}</h3>
                <small>Completed</small>
              </div>
            </div>
          </div>

          {/* Action Items List - YOUR ORIGINAL CODE */}
          <div className="dashboard-card">
            {loading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading action items...</p>
              </div>
            ) : actionItems.length > 0 ? (
              <div>
                {actionItems.map(item => (
                  <div key={item.id} className="action-item-card" style={{
                    borderLeft: `4px solid ${getStatusColor(item.status)}`
                  }}>
                    {editingItem === item.id ? (
                      // Edit Mode - YOUR ORIGINAL EDIT FORM
                      <div>
                        <div style={{ display: 'grid', gap: '10px' }}>
                          <div>
                            <label style={{ fontSize: '12px', color: '#666' }}>Description</label>
                            <textarea
                              value={updateData.description}
                              onChange={(e) => setUpdateData({...updateData, description: e.target.value})}
                              className="form-input"
                              style={{ minHeight: '60px' }}
                            />
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <div>
                              <label style={{ fontSize: '12px', color: '#666' }}>Assigned To</label>
                              <input
                                type="text"
                                value={updateData.assigned_to}
                                onChange={(e) => setUpdateData({...updateData, assigned_to: e.target.value})}
                                className="form-input"
                              />
                            </div>

                            <div>
                              <label style={{ fontSize: '12px', color: '#666' }}>Status</label>
                              <select
                                value={updateData.status}
                                onChange={(e) => setUpdateData({...updateData, status: e.target.value})}
                                className="form-input"
                              >
                                <option value="pending">Pending</option>
                                <option value="in_progress">In Progress</option>
                                <option value="completed">Completed</option>
                              </select>
                            </div>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <div>
                              <label style={{ fontSize: '12px', color: '#666' }}>Priority</label>
                              <select
                                value={updateData.priority}
                                onChange={(e) => setUpdateData({...updateData, priority: e.target.value})}
                                className="form-input"
                              >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                              </select>
                            </div>

                            <div>
                              <label style={{ fontSize: '12px', color: '#666' }}>Due Date</label>
                              <input
                                type="date"
                                value={updateData.due_date}
                                onChange={(e) => setUpdateData({...updateData, due_date: e.target.value})}
                                className="form-input"
                              />
                            </div>
                          </div>

                          {updateData.status === 'completed' && (
                            <div>
                              <label style={{ fontSize: '12px', color: '#666' }}>Completion Notes</label>
                              <textarea
                                value={updateData.completion_notes}
                                onChange={(e) => setUpdateData({...updateData, completion_notes: e.target.value})}
                                placeholder="Add notes about how this was completed..."
                                className="form-input"
                                style={{ minHeight: '50px' }}
                              />
                            </div>
                          )}

                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => handleUpdate(item.id)}
                              className="btn-success"
                            >
                              Save Changes
                            </button>
                            <button
                              onClick={() => {
                                setEditingItem(null);
                                setUpdateData({});
                              }}
                              className="btn-secondary"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // View Mode - YOUR ORIGINAL VIEW
                      <div>
                        <div className="action-item-container">
                          <div style={{ flex: 1 }}>
                            <div className="action-item-title" style={{
                              textDecoration: item.status === 'completed' ? 'line-through' : 'none'
                            }}>
                              {item.description}
                            </div>
                            
                            <div className="action-item-details">
                              <div>
                                <strong>Meeting:</strong> {item.meetings?.title || 'N/A'}
                              </div>
                              <div>
                                <strong>Date:</strong> {item.meetings?.meeting_date ? 
                                  new Date(item.meetings.meeting_date).toLocaleDateString() : 'N/A'}
                              </div>
                              <div>
                                <strong>Assigned:</strong> {item.assigned_to || 'All'}
                              </div>
                              {item.due_date && (
                                <div>
                                  <strong>Due:</strong> {new Date(item.due_date).toLocaleDateString()}
                                </div>
                              )}
                            </div>

                            {item.completion_notes && (
                              <div className="completion-notes">
                                <strong>Completion Notes:</strong> {item.completion_notes}
                              </div>
                            )}

                            <div className="action-item-badges">
                              <span className="status-badge" style={{
                                background: getStatusColor(item.status)
                              }}>
                                {item.status?.toUpperCase()}
                              </span>
                              <span className="priority-badge" style={{
                                background: getPriorityColor(item.priority)
                              }}>
                                {item.priority?.toUpperCase()} PRIORITY
                              </span>
                            </div>
                          </div>

                          <div className="action-item-buttons">
                            {item.status !== 'completed' && (
                              <>
                                {item.status === 'pending' && (
                                  <button
                                    onClick={() => handleQuickStatusUpdate(item.id, 'in_progress')}
                                    className="btn-info"
                                  >
                                    Start Progress
                                  </button>
                                )}
                                <button
                                  onClick={() => handleQuickStatusUpdate(item.id, 'completed')}
                                  className="btn-success"
                                >
                                  Mark Complete
                                </button>
                              </>
                            )}
                            
                            <button
                              onClick={() => handleEdit(item)}
                              className="btn-primary"   
                            >
                              Edit
                            </button>
                            
                            {userData?.role === 'superadmin' && (
                              <button
                                onClick={() => handleDelete(item.id)}
                                className="btn-danger"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                No action items found
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default ActionItemsManagement;